import { Model, Factory, Response } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randPhrase, randBoolean, randSentence, rand } from '@ngneat/falso'
import { snakeCase } from 'change-case'

import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import {
  PaginationError,
  createErrorResponse,
  filterable,
  pageable,
} from './utils.js'

export type CentralizedSettingsData = SchemaAttributes<'CentralizedSetting'>

export const model = Model as ModelDefinition<CentralizedSettingsData>

const sections = ['General', 'Organization', 'Department']

export const factory = Factory.extend<CentralizedSettingsData>({
  name() {
    return snakeCase(randPhrase({ maxCharCount: 50 }))
  },
  label() {
    return randPhrase({ maxCharCount: 60 })
  },
  help_text() {
    return randBoolean() ? randSentence() : undefined
  },
  centralized() {
    return randBoolean()
  },
  section() {
    return snakeCase(rand(sections))
  },
})

export function routes(server: MockServer) {
  server.resource('centralizedSetting', { path: '/centralized_settings' })

  server.get(
    '/centralized_settings',
    function (this: MirageServer, schema, request) {
      const centralizedSettings = schema.all('centralizedSetting')

      // for now we won't be filtering the centralized settings
      const filtered = filterable(centralizedSettings, request, {})

      // then paginate
      const { data, meta } = pageable(filtered, request)

      try {
        const sectionLabels: { [k: string]: string } = {}
        sections.forEach((section) => {
          if (sectionLabels[snakeCase(section)]) {
            return
          }

          sectionLabels[snakeCase(section)] = section
        })

        return {
          ...this.serialize(data),
          meta: {
            pagination: meta,
            section_labels: sectionLabels,
          },
        }
      } catch (e) {
        if (e instanceof PaginationError) {
          return new Response(404, {}, e.message)
        }

        // Fallback for unexpected errors
        return new Response(500, {}, 'Internal Server Error')
      }
    },
  )

  server.get('/centralized_settings/:id', function (schema, request) {
    const id = request.params.id
    const centralizedSetting = schema.find('centralizedSetting', id)

    if (!centralizedSetting) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Centralized Setting with id ${id} not found` }],
      })
    }

    return centralizedSetting
  })

  server.patch('/centralized_settings/:id', async function (schema, request) {
    const id = request.params.id
    const attrs = JSON.parse(request.requestBody)
    const centralizedSetting = schema.find('centralizedSetting', id)

    if (!centralizedSetting) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `Centralized Setting with id ${id} not found` }],
      })
    }

    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }

    await centralizedSetting.update(attrs.data.attributes)
    return centralizedSetting
  })
}

export function seeds(server: MockServer) {
  server.createList('centralizedSetting', 20)
}
